import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { WifiOff } from 'lucide-react';
import { useOnlineStatus } from '@/shared/hooks/use-online-status';
import { useToast } from '@/shared/ui/toast';

/**
 * Offline indicator.
 *
 * Nothing is served from cache — see the `NetworkOnly` rule in `vite.config.ts`
 * — so the shell stays usable offline but the data does not. Saying that
 * plainly is better than showing a stale stock balance that a storekeeper
 * would act on.
 */
export function OfflineBanner() {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const toast = useToast();
  const wasOffline = useRef(false);

  useEffect(() => {
    if (!online) {
      wasOffline.current = true;
      return;
    }

    if (wasOffline.current) {
      wasOffline.current = false;
      toast.success(t('pwa.online'));
    }
  }, [online, toast, t]);

  if (online) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-warning text-warning-foreground flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium"
    >
      <WifiOff className="size-3.5 shrink-0" aria-hidden />
      <span>{t('pwa.offlineTitle')}</span>
      <span className="hidden sm:inline">— {t('pwa.offlineBody')}</span>
    </div>
  );
}
